"use client";

import Link from "next/link";
import { useUI } from "@/lib/context/UIProvider";
import { useAuth } from "@/lib/context/AuthProvider";
import { LanguageMenu } from "@/components/layout/LanguageMenu";
import { Skeleton } from "@/components/ui/Skeleton";

/** Sticky top bar: menu trigger, centered logo, language switcher. */
export function Header() {
  const { openSidebar } = useUI();
  const { isPro, hydrated } = useAuth();
  // Gate on auth boot only. Tying this to `isPageLoading` too made the header
  // fall back to skeletons on every route change, and the placeholders are a
  // different width than the real controls — a visible shift each navigation.
  const chromeReady = hydrated;

  return (
    <header className="glass-header sticky top-0 z-50 flex items-center justify-between gap-3 rounded-b-2xl border-b border-l border-r border-[rgba(26,24,22,0.06)] px-5 py-[13px] md:rounded-b-[26px]">
      <div className="flex flex-1 justify-start">
        {!chromeReady ? (
          <div
            className="h-8 w-9 rounded-lg bg-[rgba(26,24,22,0.06)]"
            aria-hidden
          />
        ) : (
          <button
            type="button"
            onClick={openSidebar}
            aria-label="Menu"
            className="flex h-8 w-9 items-center justify-center rounded-lg text-ink-3 transition-opacity hover:opacity-85"
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4.5" width="18" height="15" rx="3.5" />
              <path d="M9.5 4.5v15" />
              <path d="M5.7 9.2h1.6" />
              <path d="M5.7 12.4h1.6" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex flex-none items-center gap-2.5">
        <Link href="/tools" aria-label="psy.day" className="relative inline-flex">
          <img
            src="/favicon.png"
            alt="psy.day"
            width={30}
            height={30}
            className="block h-[30px] w-[30px] rounded-lg"
          />
          {chromeReady ? (
            <span className="pointer-events-none absolute -bottom-[5px] left-1/2 -translate-x-1/2 rounded-full border border-gold bg-accent px-[5px] py-[1.5px] text-[7.5px] font-bold leading-none tracking-[0.5px] text-white shadow-[0_1px_2px_rgba(26,24,22,0.18)]">
              {isPro ? "PRO" : "FREE"}
            </span>
          ) : (
            <Skeleton className="pointer-events-none absolute -bottom-[5px] left-1/2 h-[12px] w-[28px] -translate-x-1/2 rounded-full bg-[rgba(26,24,22,0.12)]" />
          )}
        </Link>
      </div>

      <div className="flex flex-1 justify-end">
        <LanguageMenu locked={!chromeReady} />
      </div>
    </header>
  );
}
